import { UseQueryOptions, keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import {
	type AddUsersToSelectionProps,
	type GetUserSelectionUsersProps,
	type RemoveUsersFromSelectionProps,
	type UserSelectionCountResponse,
	type UserSelectionMutationResponse,
	type UserSelectionUsersResponse,
	addUsersToSelection,
	createUserSelection,
	getUserSelectionCount,
	getUserSelectionUsers,
	removeUsersFromSelection,
} from "../../services/defs/user-selection";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

/**
 * Hook to create a new user selection object
 */
export const useCreateUserSelection = (options?: PYUseMutationOptions<{ user_selection_id: string }, Error, void>) => {
	return useMutation({
		mutationFn: createUserSelection,
		meta: {
			errorMessage: "There was an error creating the user selection.",
		},
		onSuccess: (data, variables, context, isPreviousData) => {
			options?.onSuccess?.(data, variables, context, isPreviousData);
		},
		...options,
	});
};

export const userSelectionUsersQuery = (
	props: GetUserSelectionUsersProps,
	options?: PYUseQueryOptions<UserSelectionUsersResponse>
): UseQueryOptions<UserSelectionUsersResponse> => {
	const { selectionObjectID, page, limitSelected, ...rest } = props;
	return {
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ["user-selection-users", selectionObjectID, page, limitSelected, rest],
		queryFn: () => getUserSelectionUsers(props),
		meta: {
			errorMessage: "There was an error getting the users for this selection.",
		},
		enabled: !!props.selectionObjectID,
		placeholderData: keepPreviousData,
		...options,
	};
};

/**
 * Hook to get users for a user selection object with search/filter capabilities
 */
export const useUserSelectionUsers = (
	props: GetUserSelectionUsersProps,
	options?: PYUseQueryOptions<UserSelectionUsersResponse>
) => {
	return useQuery(userSelectionUsersQuery(props, options));
};

/**
 * Hook to get users for a user selection object with infinite scrolling
 */
export const useUserSelectionUsersInfinite = (props: Omit<GetUserSelectionUsersProps, "page">) => {
	const { selectionObjectID, limitSelected, ...rest } = props;

	return useInfiniteQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ["user-selection-users-infinite", selectionObjectID, limitSelected, rest],
		queryFn: ({ pageParam }) => getUserSelectionUsers({ ...props, page: pageParam }),
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			const currentPage = allPages.length;
			const hasMore = currentPage < lastPage.page_count;
			return hasMore ? currentPage + 1 : undefined;
		},
		meta: {
			errorMessage: "There was an error getting the users for this selection.",
		},
		enabled: !!props.selectionObjectID,
	});
};

/**
 * Hook to add users to a user selection object
 */
export const useAddUsersToSelection = (
	options?: PYUseMutationOptions<UserSelectionMutationResponse, Error, AddUsersToSelectionProps>
) => {
	return useMutation({
		mutationFn: addUsersToSelection,
		meta: {
			errorMessage: "There was an error adding users to the selection.",
		},
		...options,
	});
};

/**
 * Hook to remove users from a user selection object
 */
export const useRemoveUsersFromSelection = (
	options?: PYUseMutationOptions<UserSelectionMutationResponse, Error, RemoveUsersFromSelectionProps>
) => {
	return useMutation({
		mutationFn: removeUsersFromSelection,
		meta: {
			errorMessage: "There was an error removing users from the selection.",
		},
		...options,
	});
};

/**
 * Hook to get the count of selected users in a user selection object
 */
export const useUserSelectionCount = (
	selectionObjectID: string,
	options?: PYUseQueryOptions<UserSelectionCountResponse>
) => {
	return useQuery({
		queryKey: ["user-selection-count", selectionObjectID],
		queryFn: () => getUserSelectionCount(selectionObjectID),
		meta: {
			errorMessage: "There was an error getting the user selection count.",
		},
		enabled: !!selectionObjectID,
		...options,
	});
};
